'use client';

import { useState, type ReactNode } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../components/styled/text';
import { Collapsible } from '../../components/collapsible';
import {
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowRight01Icon,
} from '@terra/ui-icons';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { useTranslations } from 'next-intl';
import { HttpEditor } from '../http-editor';
import { MarkdownPreview } from '../markdown-preview';
import { EvidenceArtifactContentSection } from './EvidenceArtifactContentSection';
import { EvidenceCardMediaSection } from './EvidenceCardMediaSection';
import { EvidenceHarExchangeRow } from './EvidenceHarExchangeRow';

export type EvidenceCardWithHarProps = {
  toolExecutionHarId?: string;
  redirectHarIds?: string[];
  evidenceType?: 'http' | 'artifact' | 'email' | 'websocket' | 'oob';
  explanation?: string;
  harData?: ToolExecutionHar | null;
  manualRequest?: string;
  manualResponse?: string;
  canonicalRequestUrl?: string;
  index: number;
  defaultExpanded?: boolean;
  actions?: ReactNode;
  media?: ReactNode;
  escapeExplanationRawHtml?: boolean;
};
export function EvidenceCardWithHar({
  toolExecutionHarId,
  redirectHarIds,
  evidenceType,
  explanation,
  harData,
  manualRequest,
  manualResponse,
  canonicalRequestUrl,
  index,
  defaultExpanded = false,
  actions,
  media,
  escapeExplanationRawHtml = false,
}: EvidenceCardWithHarProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const t = useTranslations('designSystem.signals.traces.evidenceCard');
  const tArtifacts = useTranslations('designSystem.artifacts');
  const hopIds = toolExecutionHarId
    ? [toolExecutionHarId, ...(redirectHarIds ?? [])]
    : [];
  const isRedirectChain = hopIds.length > 1;
  const hasManualHttpData = !!manualRequest || !!manualResponse;
  const isArtifact = evidenceType === 'artifact';

  if (!toolExecutionHarId) {
    const typeLabel = tArtifacts(evidenceType ?? 'http');

    return (
      <Box
        borderWidth="1px"
        borderColor="border.neutral.muted"
        borderRadius="md"
        bg="bg.subtle"
        overflow="hidden"
        width="100%"
      >
        <HStack
          justify="space-between"
          alignItems="center"
          px={3}
          h="36px"
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderBottomColor="border.neutral.muted"
        >
          <HStack gap={1.5} alignItems="center" minW={0}>
            <Text fontSize="xs" fontWeight="medium" color="fg.default" truncate>
              {tArtifacts('evidenceLabel', { index: index + 1 })}
            </Text>
            <Text fontSize="xs" color="fg.muted" truncate>
              {typeLabel}
            </Text>
          </HStack>
          {actions ? (
            <HStack gap={1} alignItems="center">
              {actions}
            </HStack>
          ) : null}
        </HStack>

        {explanation ? (
          <Box
            bg="bg.subtle"
            px={3}
            py={2}
            color="fg.default"
            borderBottomWidth="1px"
            borderBottomColor="border.neutral.muted"
          >
            <MarkdownPreview
              source={explanation}
              fontSize={12}
              compact
              escapeRawHtml={escapeExplanationRawHtml}
            />
          </Box>
        ) : null}

        {hasManualHttpData ? (
          <Collapsible.Root
            open={isExpanded}
            onOpenChange={({ open }) => setIsExpanded(open)}
          >
            <Collapsible.Trigger asChild>
              <HStack
                w="full"
                gap={1.5}
                px={3}
                h="32px"
                cursor="pointer"
                bg="bg.subtle"
                _hover={{ bg: 'bg.muted' }}
                alignItems="center"
              >
                <Box display="flex" alignItems="center" color="fg.muted">
                  {isExpanded ? (
                    <ArrowDown01Icon size={10} />
                  ) : (
                    <ArrowRight01Icon size={10} />
                  )}
                </Box>
                <Text fontSize="xs" fontWeight="medium" color="fg.default">
                  {isArtifact ? t('action') : t('request')}
                </Text>
                {!isArtifact && manualResponse ? (
                  <Text fontSize="xs" color="fg.muted">
                    / {t('response')}
                  </Text>
                ) : null}
              </HStack>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Box
                borderTopWidth="1px"
                borderColor="border.neutral.muted"
                bg="bg.subtle"
              >
                {isArtifact ? (
                  <VStack gap={0} w="full" alignItems="stretch">
                    {manualRequest ? (
                      <EvidenceArtifactContentSection
                        label={t('action')}
                        content={manualRequest}
                      />
                    ) : null}
                    {manualResponse ? (
                      <EvidenceArtifactContentSection
                        label={t('result')}
                        content={manualResponse}
                      />
                    ) : null}
                  </VStack>
                ) : (
                  <HStack alignItems="stretch" gap="0" width="100%" h="400px">
                    <Box flex="1" minH={0} minW={0} h="full">
                      <HttpEditor
                        value={manualRequest ?? ''}
                        title={t('request')}
                        readOnly
                        showViewModeToggle
                        canonicalRequestUrl={canonicalRequestUrl}
                      />
                    </Box>
                    <Box w="1px" bg="border.neutral.muted" flexShrink={0} />
                    <Box flex="1" minH={0} minW={0} h="full">
                      <HttpEditor
                        value={manualResponse ?? ''}
                        title={t('response')}
                        readOnly
                        showViewModeToggle
                      />
                    </Box>
                  </HStack>
                )}
              </Box>
            </Collapsible.Content>
          </Collapsible.Root>
        ) : !media ? (
          <HStack w="full" gap={2} px={3} py={2} bg="bg.subtle">
            <HStack gap={2} flex={1} minW={0} color="fg.muted">
              <AlertCircleIcon size={14} />
              <Text fontSize="xs" color="fg.muted">
                {tArtifacts('evidenceNotAvailable', { index: index + 1 })}
              </Text>
            </HStack>
          </HStack>
        ) : null}

        <EvidenceCardMediaSection media={media} />
      </Box>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="bg.subtle"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="bg.subtle"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Text fontSize="xs" fontWeight="medium" color="fg.default" truncate>
            {tArtifacts('evidenceLabel', { index: index + 1 })}
          </Text>
          <Text fontSize="xs" color="fg.muted" truncate>
            {tArtifacts('http')}
          </Text>
        </HStack>
        {actions && (
          <HStack gap={1} alignItems="center">
            {actions}
          </HStack>
        )}
      </HStack>

      {explanation && (
        <Box
          bg="bg.subtle"
          px={3}
          py={2}
          color="fg.default"
          borderBottomWidth="1px"
          borderBottomColor="border.neutral.muted"
        >
          <MarkdownPreview
            source={explanation}
            fontSize={12}
            compact
            escapeRawHtml={escapeExplanationRawHtml}
          />
        </Box>
      )}

      <VStack gap={0} alignItems="stretch">
        {hopIds.map((hopId, hopIndex) => (
          <EvidenceHarExchangeRow
            key={hopId}
            toolExecutionHarId={hopId}
            harData={hopIndex === 0 ? harData : undefined}
            defaultExpanded={hopIndex === 0 ? defaultExpanded : false}
            hopLabel={
              isRedirectChain
                ? hopIndex === 0
                  ? t('anchorHop')
                  : t('redirectHop', { index: hopIndex })
                : undefined
            }
          />
        ))}
      </VStack>
    </Box>
  );
}
